import React, { useEffect, useState } from 'react';
import { ArrowLeft, Sparkles, Check, X, Loader2, Settings, Receipt, Users, Search, Trash2, CreditCard, Link2, Apple, Eye, EyeOff } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ThankYouCelebration } from '@/components/ThankYouCelebration';
import PromoCodesSection from '@/components/PromoCodesSection';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { PaymentLinkModal } from '@/components/PaymentLinkModal';
import SoundLineage from '@/components/SoundLineage';
import AdminSupportInbox from '@/components/AdminSupportInbox';
import AdminCancellationsInbox from '@/components/AdminCancellationsInbox';
import AdminEmailEngagement from '@/components/AdminEmailEngagement';
import AdminSMSStats from '@/components/AdminSMSStats';
import CancelAccountModal from '@/components/CancelAccountModal';
import CancelSubscriptionModal from '@/components/CancelSubscriptionModal';
import AdminFrequencyDefaults from '@/components/AdminFrequencyDefaults';
import AdminUserProfileModal from '@/components/AdminUserProfileModal';
import { UserCog } from 'lucide-react';
import NudgePreferencesCard from '@/components/NudgePreferencesCard';
import HarmonicBlueprintSection from '@/components/HarmonicBlueprintSection';
import MyJourneySection from '@/components/MyJourneySection';
import MyPatternsSection from '@/components/MyPatternsSection';
import AnnouncementComposerSection from '@/components/AnnouncementComposerSection';
import { usePaymentMethodSupport } from '@/hooks/usePaymentMethodSupport';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch (e) { console.warn('[AccountDashboard] fmtDate failed', e); return iso; }
}

function fmtMoney(amount, currency = 'usd') {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount); }
  catch (e) { console.warn('[AccountDashboard] fmtMoney failed', e); return `$${amount}`; }
}

export default function AccountDashboard({ onBack, onOpenHarmonicBlueprint }) {
  const { user, setUserName } = useAuth();
  // Global subscription context — must be refreshed after any entitlement
  // change (promo redemption, Stripe checkout return) so Dashboard's isPro
  // flag flips without requiring a page reload.
  const { refresh: refreshGlobalSub } = useSubscription();
  const [sub, setSub] = useState(null);
  const [plan, setPlan] = useState(null);
  const [tx, setTx] = useState([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // name edit
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  // password
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  // Show/hide toggles for the Change Password inputs — off by default for
  // shoulder-surf safety; user can flip either independently to verify the
  // exact string they typed on flaky mobile keyboards.
  const [showCurPw, setShowCurPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // post-payment celebration
  const [celebratingPlan, setCelebratingPlan] = useState(null);

  // upgrade UI state
  const [selectedPlan, setSelectedPlan] = useState('monthly');     // 'monthly' | 'annual'
  // Promo code state — user-facing entry on the paywall. `validation` holds
  // the /promo/validate response so we can render the "what this unlocks"
  // confirmation card before the user commits.
  const [promoInput, setPromoInput] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);   // {valid, type, summary, ...}
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoErr, setPromoErr] = useState('');
  const [compRedeemed, setCompRedeemed] = useState(false);        // triggers ThankYouCelebration
  const [paymentLinkUrl, setPaymentLinkUrl] = useState(null);      // shown in PaymentLinkModal
  const paymentSupport = usePaymentMethodSupport();

  // admin price form
  const [monthly, setMonthly] = useState('');
  const [annual, setAnnual] = useState('');
  const [trial, setTrial] = useState('');

  // admin users panel — paginated so we can page through every registered
  // user rather than truncating at the top 100 rows. Test seeds
  // (@example.com) are hidden by default; toggle `showTestUsers` to
  // include them.
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [grantDays, setGrantDays] = useState({});
  const [usersMeta, setUsersMeta] = useState({ total: 0, offset: 0, limit: 100, filtered_test_count: 0 });
  const [showTestUsers, setShowTestUsers] = useState(false);
  // Admin: open a full profile-editor modal against a specific user_id. When
  // set, `AdminUserProfileModal` mounts and fetches that user's editable
  // profile. `null` = modal closed.
  const [profileModalUserId, setProfileModalUserId] = useState(null);

  // admin security tile — counters + recent audit events + new-user badge
  const [security, setSecurity] = useState(null);
  // Range for the "Recent Activity" feed. `null` = default snapshot (12 most
  // recent items across all time). 7/30/90 = filter by that many days AND
  // lift the cap to 300 items for browsable history.
  const [activityRangeDays, setActivityRangeDays] = useState(null);
  const loadSecurity = async (rangeDays = activityRangeDays) => {
    try {
      const params = rangeDays ? { days: rangeDays } : {};
      const { data } = await api.get('/admin/security', { params });
      setSecurity(data);
    } catch (e) {
      console.warn('[AccountDashboard] loadSecurity failed', e);
    }
  };

  const loadUsers = async (q = '', offset = 0, includeTest = showTestUsers) => {
    try {
      const params = {
        offset,
        limit: 100,
        include_test: includeTest ? 'true' : 'false',
      };
      if (q) params.q = q;
      const { data } = await api.get('/admin/users', { params });
      // Server switched from a bare list → {items,total,offset,limit,...}.
      // Support the legacy shape too so a mid-deploy hiccup doesn't wipe
      // the panel.
      if (Array.isArray(data)) {
        setUsers(data);
        setUsersMeta({ total: data.length, offset: 0, limit: data.length, filtered_test_count: 0 });
      } else {
        setUsers(data?.items || []);
        setUsersMeta({
          total: data?.total || 0,
          offset: data?.offset || 0,
          limit: data?.limit || 100,
          filtered_test_count: data?.filtered_test_count || 0,
        });
      }
    } catch (e) { console.warn('[AccountDashboard] loadUsers failed (likely non-admin)', e); }
  };

  const load = async () => {
    try {
      const [s, p, t] = await Promise.all([
        api.get('/me/subscription'),
        api.get('/plan/config'),
        api.get('/me/transactions'),
      ]);
      setSub(s.data); setPlan(p.data); setTx(t.data);
      if (s.data.is_admin) {
        const ap = await api.get('/admin/plan-prices');
        setMonthly(String(ap.data.monthly.price));
        setAnnual(String(ap.data.annual.price));
        setTrial(String(ap.data.trial_days));
        await Promise.all([loadUsers(), loadSecurity()]);
      }
    } catch (e) { setErr(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Admin security tile auto-refresh — poll every 30s while the tab is open.
  // Light query (single GET, no Stripe call) so it's safe to keep alive.
  // Re-subscribes when the activity range changes so the poll stays in sync
  // with the selected 7d / 30d / 90d window.
  useEffect(() => {
    if (!sub?.is_admin) return undefined;
    const id = setInterval(() => { loadSecurity(activityRangeDays); }, 30000);
    return () => clearInterval(id);
  }, [sub?.is_admin, activityRangeDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Stripe return: ?stripe_session_id=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('stripe_session_id');
    const canceled = params.get('stripe_canceled');
    if (canceled) {
      setMsg('Checkout canceled — no charges made.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (sid) {
      setBusy('polling');
      setMsg('Verifying payment…');
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === 'paid') {
            setMsg('Payment confirmed — welcome to Pro!');
            setBusy('');
            window.history.replaceState({}, '', window.location.pathname);
            // Celebrate! Use the plan stored on the transaction (monthly/annual) for the label.
            const planRaw = data.plan || data.metadata?.plan || '';
            const planLabel = planRaw === 'annual' ? 'Pro Annual' : (planRaw === 'monthly' ? 'Pro Monthly' : 'Pro');
            setCelebratingPlan(planLabel);
            load();
            return;
          }
          if (data.status === 'expired' || attempts > 8) {
            setMsg('Payment did not complete. If you were charged, contact support.');
            setBusy('');
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          setErr(formatApiError(e));
          setBusy('');
        }
      };
      poll();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openBillingPortal = async () => {
    setBusy('portal'); setErr(''); setMsg('');
    try {
      const { data } = await api.post('/me/billing-portal', { return_url: window.location.href });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setErr('Billing portal could not be opened. Try again in a moment.');
        setBusy('');
      }
    } catch (e) {
      setErr(formatApiError(e));
      setBusy('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const cancelSubscription = async () => {
    // HF-049: open the exit-survey modal instead of a bare confirm.
    setCancelSubModalOpen(true);
  };
  const [cancelSubModalOpen, setCancelSubModalOpen] = useState(false);
  const onSubscriptionCancelled = async () => {
    setCancelSubModalOpen(false);
    setMsg("Subscription canceled. You'll keep Pro access until your current period ends.");
    await load();
  };

  // HF-043 Cancel service (close account) — separate from cancelSubscription.
  // Uses a two-step confirmation modal (see `<CancelAccountModal>` below) so
  // it can't fire accidentally: user must type "close" AND press the button.
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const cancelAccount = async (reason) => {
    setBusy('cancel-account'); setErr(''); setMsg('');
    try {
      await api.post('/me/cancel-account', { reason: reason || null });
      // Server has already invalidated our bearer token — a `.get('/auth/me')`
      // will now 401. Blow local auth state and route to the sign-in screen.
      try { localStorage.removeItem('token'); } catch (_) { /* graceful */ }
      try { localStorage.removeItem('solar:user'); } catch (_) { /* graceful */ }
      // Give the user a moment to see the confirmation copy, then hard-reload
      // so App.jsx's auth bootstrap redirects them to the landing screen.
      setMsg('Your account is closed. You\'ll continue to receive occasional emails and texts about coming back — reply STOP or use the email unsubscribe link to stop those. Redirecting to the sign-in page…');
      setTimeout(() => { window.location.href = '/'; }, 2600);
    } catch (e) {
      setErr(formatApiError(e));
      setBusy('');
    }
  };

  /**
   * Start a checkout for the currently-selected plan.
   * @param {'card'|'apple_pay'|'google_pay'|'link'} method — informs both the
   *   metadata sent to Stripe (for analytics) and HOW we present the resulting
   *   URL: 'card' / 'apple_pay' / 'google_pay' redirect this device to Stripe
   *   Checkout (which auto-renders the AP/GP buttons when supported); 'link'
   *   shows a QR + copy UI so the user can pay on another device.
   */
  const upgrade = async (method = 'card') => {
    const busyKey = `${selectedPlan}-${method}`;
    setBusy(busyKey); setErr(''); setMsg('');
    try {
      const { data } = await api.post('/me/checkout', {
        plan: selectedPlan,
        origin_url: window.location.origin,
        payment_method_preference: method,
        // Forward any validated promo code so discount / referral flows are
        // applied inside Stripe (and our redemption counter increments).
        // Comp codes never reach here — they short-circuit via redeemPromo().
        promo_code: promoValidation?.valid && promoValidation.type !== 'comp'
          ? promoInput.trim().toUpperCase()
          : undefined,
      });
      if (!data?.url) {
        setErr('Checkout could not start: no Stripe URL returned. Check the server logs and your STRIPE_API_KEY.');
        setBusy('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (method === 'link') {
        setPaymentLinkUrl(data.url);
        setBusy('');
      } else {
        // Apple Pay, Google Pay and Card all redirect to the same Stripe Checkout
        // URL. Stripe Checkout auto-renders Apple Pay / Google Pay buttons inside
        // its hosted page when the browser supports them — graceful degradation
        // to card form when it doesn't.
        window.location.href = data.url;
      }
    } catch (e) {
      setErr(formatApiError(e));
      setBusy('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ---- Promo Code entry (paywall) ---------------------------------------
  const validatePromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoBusy(true); setPromoErr(''); setPromoValidation(null);
    try {
      const { data } = await api.post('/promo/validate', { code });
      if (!data.valid) {
        setPromoErr(data.reason || "That code doesn't seem to work. Double-check it or try another.");
      } else {
        setPromoValidation(data);
      }
    } catch (e) {
      setPromoErr("We couldn't check that code just now — please try again in a moment.");
    } finally {
      setPromoBusy(false);
    }
  };

  const redeemPromo = async () => {
    if (!promoValidation?.valid) return;
    if (promoValidation.type !== 'comp') return;
    setPromoBusy(true); setPromoErr('');
    try {
      await api.post('/promo/redeem', { code: promoInput.trim().toUpperCase() });
      // Refresh BOTH the local page state AND the global SubscriptionContext
      // so the Dashboard route sees isPro=true the moment the user closes
      // the ThankYouCelebration overlay. Without the global refresh, every
      // Pro-locked panel remains gated until a full page reload.
      await load();
      await refreshGlobalSub();
      setCompRedeemed(true);
      setPromoInput('');
      setPromoValidation(null);
    } catch (e) {
      setPromoErr(formatApiError(e) || 'Could not redeem this code.');
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => {
    setPromoInput('');
    setPromoValidation(null);
    setPromoErr('');
  };


  const changePassword = async (e) => {
    e.preventDefault();
    setBusy('pw'); setErr(''); setMsg('');
    try {
      await api.post('/me/password', { current_password: curPw, new_password: newPw });
      setMsg('Password updated.');
      setCurPw(''); setNewPw('');
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(''); }
  };

  const startEditName = () => {
    setNameDraft(user?.name || '');
    setEditingName(true);
    setErr(''); setMsg('');
  };

  const cancelEditName = () => {
    setEditingName(false);
    setNameDraft('');
  };

  const saveName = async (e) => {
    e.preventDefault();
    const trimmed = nameDraft.trim();
    if (!trimmed) { setErr('Name cannot be empty'); return; }
    if (trimmed === user?.name) { setEditingName(false); return; }
    setBusy('name'); setErr(''); setMsg('');
    try {
      const { data } = await api.put('/me/profile', { name: trimmed });
      setUserName(data.name);
      setMsg('Name updated.');
      setEditingName(false);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(''); }
  };

  const saveAdminPrices = async (e) => {
    e.preventDefault();
    setBusy('admin'); setErr(''); setMsg('');
    try {
      const { data } = await api.put('/admin/plan-prices', {
        monthly_price: parseFloat(monthly),
        annual_price: parseFloat(annual),
        trial_days: parseInt(trial, 10),
      });
      // Use the PUT response directly — the canonical post-save config —
      // instead of a stale re-GET. Eliminates any race or cache surprise.
      setMonthly(String(data.monthly.price));
      setAnnual(String(data.annual.price));
      setTrial(String(data.trial_days));
      // Also refresh the public plan config so the upgrade cards reflect new prices
      try {
        const { data: planData } = await api.get('/plan/config');
        setPlan(planData);
      } catch (e) { console.warn('[AccountDashboard] plan/config refresh failed', e); }
      setMsg(`Saved · Monthly $${data.monthly.price.toFixed(2)} · Annual $${data.annual.price.toFixed(2)} · ${data.trial_days}-day trial`);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(''); }
  };

  const grantPro = async (uid) => {
    const days = parseInt(grantDays[uid] || '365', 10);
    setBusy(`grant-${uid}`); setErr(''); setMsg('');
    try {
      const { data } = await api.post(`/admin/users/${uid}/grant-pro`, { days });
      setMsg(`Granted Pro to ${data.email} (+${data.days_added} days).`);
      await loadUsers(userQuery);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(''); }
  };

  const revokePro = async (uid, email) => {
    setBusy(`revoke-${uid}`); setErr(''); setMsg('');
    try {
      await api.post(`/admin/users/${uid}/revoke-pro`);
      setMsg(`Revoked Pro from ${email}.`);
      await loadUsers(userQuery);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(''); }
  };

  const deleteUser = async (uid, email) => {
    if (!window.confirm(`Delete user ${email}? This permanently removes their account, sessions, streak and billing history. This cannot be undone.`)) return;
    setBusy(`del-${uid}`); setErr(''); setMsg('');
    try {
      await api.delete(`/admin/users/${uid}`);
      setMsg(`Deleted ${email}.`);
      await loadUsers(userQuery);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(''); }
  };

  const searchUsers = (e) => {
    e.preventDefault();
    loadUsers(userQuery);
  };

  if (!sub || !plan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="aurora-bg" /><div className="grain" />
        <div className="relative font-display text-xl text-[#8A9A92]">Loading…</div>
      </div>
    );
  }

  const proFeatures = [
    'Unlimited saved sessions',
    'Sound Baths — 7 algorithmic crystal-bowl, chime & gong washes',
    'Meditation Sounds — curated presets, 7 chakra tones & guided breath pacers',
    'Soundscapes — 6 curated multi-layer mixes',
    'Brainwave & Specials presets (Delta, Theta, Schumann, 111, Tesla 369, Angel 444, …)',
    'φ Golden Stack harmonic mode',
    'Sleep Mode (30-min fade-to-silence)',
    'Ambient layers (rain · ocean · forest · wind · crickets · bowls · brown · white)',
    'Breathwork guide with pulsing haptics',
    'Equalizer hearing calibration (personal EQ profile)',
    'Custom frequency generator',
  ];

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden pb-20">
      <div className="aurora-bg" /><div className="grain" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 lg:px-8 py-8">
        <button
          data-testid="account-back-button"
          onClick={onBack}
          className="text-[#8A9A92] hover:text-[#72C2AC] flex items-center gap-2 mb-6 transition-colors"
        >
          <ArrowLeft size={16} /> Back to player
        </button>

        <div className="mb-8">
          <div className="label-tiny mb-2">Account</div>
          {editingName ? (
            <form onSubmit={saveName} className="flex items-center gap-3 flex-wrap" data-testid="edit-name-form">
              <input
                data-testid="edit-name-input"
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                maxLength={80}
                placeholder="Your name"
                className="font-display text-4xl font-light text-[#E8E3D9] bg-transparent border-b border-[#72C2AC]/40 focus:border-[#72C2AC] outline-none min-w-[260px] py-1"
              />
              <button
                data-testid="save-name-button"
                type="submit"
                disabled={busy === 'name'}
                className="px-4 py-1.5 rounded-full bg-[#5C9E8C] hover:bg-[#72C2AC] text-[#08120F] text-xs font-medium transition-colors disabled:opacity-50"
              >
                {busy === 'name' ? 'Saving…' : 'Save'}
              </button>
              <button
                data-testid="cancel-name-button"
                type="button"
                onClick={cancelEditName}
                className="px-3 py-1.5 rounded-full border border-[#5C9E8C]/30 text-[#8A9A92] hover:text-[#E8E3D9] text-xs transition-colors"
              >
                Cancel
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <h1 data-testid="account-greeting" className="font-display text-4xl font-light text-[#E8E3D9]">
                Hello, {user.name}
              </h1>
              <button
                data-testid="edit-name-button"
                onClick={startEditName}
                className="text-[11px] tracking-wider text-[#72C2AC] hover:text-[#C4A67A] underline-offset-2 hover:underline transition-colors"
                title="Edit name"
              >
                Edit
              </button>
            </div>
          )}
          <p className="text-sm text-[#8A9A92] mt-1">{user.email}</p>
        </div>

        {msg && <div data-testid="account-msg" className="glass-soft p-3 mb-4 text-sm text-[#72C2AC]">{msg}</div>}
        {err && <div data-testid="account-err" className="glass-soft p-3 mb-4 text-sm text-[#D96C6C]">{err}</div>}

        {/* Plan card */}
        <div className="glass p-6 mb-6" data-testid="plan-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="label-tiny mb-1">Current Plan</div>
              <div className="flex items-center gap-3">
                <div className="font-display text-3xl text-[#E8E3D9] capitalize">
                  {sub.plan === 'trial' ? 'Pro (Trial)' : sub.plan}
                </div>
                {sub.pro && <Sparkles size={20} className="text-[#C4A67A]" />}
              </div>
              {sub.pro_until && (
                <div className="text-xs text-[#8A9A92] mt-2">
                  {sub.plan === 'trial' ? 'Trial ends' : 'Renews on'} <span className="text-[#E8E3D9]">{fmtDate(sub.pro_until)}</span>
                  {' · '}{sub.days_left} day{sub.days_left === 1 ? '' : 's'} left
                </div>
              )}
              {!sub.pro && (
                <div className="text-xs text-[#8A9A92] mt-2">Up to 3 saved sessions on Basic.</div>
              )}
            </div>
          </div>

          {!sub.pro && (
            <div className="mt-5">
              {/* Plan selector */}
              <div data-testid="plan-selector" className="grid grid-cols-2 gap-3 mb-5">
                {['monthly', 'annual'].map((key) => {
                  const price = plan[key].price;
                  const active = selectedPlan === key;
                  const isAnnual = key === 'annual';
                  return (
                    <button
                      key={key}
                      data-testid={`plan-${key}-card`}
                      onClick={() => setSelectedPlan(key)}
                      aria-pressed={active}
                      className={`relative glass-soft p-4 text-left transition-all border ${
                        active
                          ? (isAnnual ? 'border-[#C4A67A]/80 ring-1 ring-[#C4A67A]/40' : 'border-[#72C2AC]/80 ring-1 ring-[#72C2AC]/40')
                          : (isAnnual ? 'border-[#C4A67A]/20 hover:border-[#C4A67A]/40' : 'border-[#72C2AC]/15 hover:border-[#72C2AC]/40')
                      }`}
                    >
                      {isAnnual && (
                        <div className="absolute top-3 right-3 text-[9px] tracking-widest text-[#C4A67A] bg-[#C4A67A]/10 px-2 py-1 rounded-full">
                          BEST VALUE
                        </div>
                      )}
                      <div className={`label-tiny ${isAnnual ? 'text-[#C4A67A]' : 'text-[#72C2AC]'}`}>{isAnnual ? 'Annual' : 'Monthly'}</div>
                      <div className="font-mono text-2xl text-[#E8E3D9] mt-1">
                        {fmtMoney(price, plan.currency)}
                        <span className="text-xs text-[#8A9A92] ml-1">/ {isAnnual ? 'year' : 'month'}</span>
                      </div>
                      <div className="text-[11px] text-[#8A9A92] mt-2">
                        {isAnnual ? `Save vs ${fmtMoney(plan.monthly.price * 12, plan.currency)}` : 'Cancel anytime'}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Payment method buttons */}
              <div data-testid="payment-methods" className="space-y-2">
                <div className="label-tiny mb-2 text-[#8A9A92]">Choose a payment method</div>

                {/* Apple Pay — only when device/browser supports it */}
                {paymentSupport.applePay && (
                  <button
                    data-testid="pay-apple-pay-button"
                    onClick={() => upgrade('apple_pay')}
                    disabled={!!busy}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#E8E3D9] hover:bg-white text-[#08120F] font-medium text-sm transition-colors disabled:opacity-50"
                  >
                    <Apple size={16} />
                    <span>Pay with Apple Pay</span>
                  </button>
                )}

                {/* Google Pay — only when device/browser supports it */}
                {paymentSupport.googlePay && (
                  <button
                    data-testid="pay-google-pay-button"
                    onClick={() => upgrade('google_pay')}
                    disabled={!!busy}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#0A1A16] hover:bg-[#0F2620] border border-[#5C9E8C]/40 text-[#E8E3D9] font-medium text-sm transition-colors disabled:opacity-50"
                  >
                    <span className="font-mono tracking-wider text-[#72C2AC]">G</span>
                    <span>Pay with Google Pay</span>
                  </button>
                )}

                {/* Card / Stripe Checkout — always available */}
                <button
                  data-testid="pay-card-button"
                  onClick={() => upgrade('card')}
                  disabled={!!busy}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#5C9E8C] hover:bg-[#72C2AC] text-[#08120F] font-medium text-sm transition-colors disabled:opacity-50"
                >
                  {busy === `${selectedPlan}-card` ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                  <span>{busy === `${selectedPlan}-card` ? 'Redirecting…' : 'Pay with Card (Stripe Checkout)'}</span>
                </button>

                {/* Payment Link — always available, cross-device */}
                <button
                  data-testid="pay-link-button"
                  onClick={() => upgrade('link')}
                  disabled={!!busy}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-transparent hover:bg-[#5C9E8C]/10 border border-[#5C9E8C]/40 text-[#72C2AC] font-medium text-sm transition-colors disabled:opacity-50"
                >
                  {busy === `${selectedPlan}-link` ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                  <span>{busy === `${selectedPlan}-link` ? 'Generating link…' : 'Get a payment link (pay on any device)'}</span>
                </button>

                {/* Capability hint — only when the device CAN do wallets but we hide them */}
                {paymentSupport.ready && !paymentSupport.applePay && !paymentSupport.googlePay && (
                  <p data-testid="wallet-unavailable-hint" className="text-[10px] text-[#8A9A92] mt-1 leading-snug">
                    Apple Pay and Google Pay aren&apos;t available on this device or browser. They appear automatically on supported devices.
                  </p>
                )}

                {/* Promo code entry — appears below the pay buttons. Comp
                    codes short-circuit checkout entirely (grants Pro directly
                    + confetti). Discount + Referral codes attach to the next
                    /me/checkout call. Styled to match the surrounding dark
                    palette + spacing conventions. */}
                <div className="mt-3 pt-3 border-t border-[#5C9E8C]/15" data-testid="promo-entry-block">
                  {!promoValidation ? (
                    <>
                      <label className="label-tiny block mb-1 text-[#8A9A92]">Have a promo code?</label>
                      <div className="flex gap-2">
                        <input
                          data-testid="promo-entry-input"
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoErr(''); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') validatePromo(); }}
                          placeholder="ENTER CODE"
                          maxLength={48}
                          className="flex-1 bg-black/30 border border-[#5C9E8C]/25 rounded-lg px-3 py-2 text-sm font-mono tracking-widest text-[#E8E3D9] focus:outline-none focus:border-[#72C2AC]/60"
                        />
                        <button
                          data-testid="promo-entry-apply"
                          onClick={validatePromo}
                          disabled={!promoInput.trim() || promoBusy}
                          className="px-4 text-[11px] uppercase tracking-widest font-mono text-[#C4A67A] hover:text-[#E8B872] border border-[#C4A67A]/30 rounded-lg disabled:opacity-40"
                        >
                          {promoBusy ? 'Checking…' : 'Apply'}
                        </button>
                      </div>
                      {promoErr && (
                        <div className="mt-2 text-[11px] text-[#D96C6C] leading-relaxed" data-testid="promo-entry-error">
                          {promoErr}
                        </div>
                      )}
                    </>
                  ) : (
                    // Confirmation card — shows what the code unlocks BEFORE
                    // the user commits. Comp codes have a dedicated "Redeem"
                    // button; discount / referral just wait for the user to
                    // pick a payment method (which forwards the code).
                    <div className="glass-soft border border-[#72C2AC]/30 p-3 rounded-xl" data-testid="promo-entry-confirmation">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="text-[11px] font-mono tracking-widest text-[#72C2AC]" data-testid="promo-entry-code">
                          {promoInput}
                        </div>
                        <button
                          onClick={clearPromo}
                          data-testid="promo-entry-clear"
                          className="text-[10px] uppercase tracking-widest font-mono text-[#8A9A92] hover:text-[#E8E3D9]"
                        >
                          Change
                        </button>
                      </div>
                      <div className="text-[12px] text-[#E8E3D9] leading-relaxed" data-testid="promo-entry-summary">
                        {promoValidation.summary}
                      </div>
                      {promoValidation.type === 'comp' && (
                        <button
                          data-testid="promo-entry-redeem"
                          onClick={redeemPromo}
                          disabled={promoBusy}
                          className="w-full mt-3 py-2.5 rounded-lg bg-[#5C9E8C]/25 hover:bg-[#5C9E8C]/40 border border-[#72C2AC]/50 hover:border-[#72C2AC] text-[#72C2AC] text-sm font-medium tracking-wide transition-colors disabled:opacity-40"
                        >
                          {promoBusy ? 'Unlocking…' : 'Redeem Free Access'}
                        </button>
                      )}
                      {promoValidation.type !== 'comp' && (
                        <div className="mt-2 text-[10px] text-[#8A9A92] leading-relaxed">
                          Applied automatically when you continue with checkout above.
                        </div>
                      )}
                      {promoErr && (
                        <div className="mt-2 text-[11px] text-[#D96C6C] leading-relaxed" data-testid="promo-entry-error">
                          {promoErr}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* Trial messaging — billing starts after 7 days, card required */}
              {!sub.trial_used && (
                <div
                  data-testid="trial-billing-info"
                  className="mt-4 p-3 rounded-lg border border-[#72C2AC]/25 bg-[#5C9E8C]/8"
                >
                  <div className="flex items-start gap-2 text-[11px] text-[#8A9A92] leading-relaxed">
                    <Sparkles size={12} className="text-[#72C2AC] mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="text-[#E8E3D9]">First {plan.trial_days} days are free.</span> A payment method is required to start — Stripe collects card details upfront but you will <span className="text-[#72C2AC]">not be charged</span> until day {plan.trial_days + 1}. Cancel anytime in those {plan.trial_days} days from Manage Billing and nothing will be charged.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Active subscription — billing management + cancel */}
          {sub.pro && !sub.is_admin && sub.has_billing_portal && (
            <div data-testid="active-sub-management" className="mt-5 space-y-2">
              {sub.in_trial && (
                <div data-testid="trial-active-banner" className="p-3 rounded-lg border border-[#C4A67A]/30 bg-[#C4A67A]/8 text-[11px] text-[#E8E3D9] leading-relaxed">
                  <div className="flex items-start gap-2">
                    <Sparkles size={12} className="text-[#C4A67A] mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="text-[#C4A67A]">Trial active.</span>{' '}
                      {sub.trial_end ? `Your card will be charged on ${fmtDate(sub.trial_end)}` : `Your trial ends in ${sub.days_left} day${sub.days_left === 1 ? '' : 's'}`}
                      {sub.cancel_at_period_end ? ' — cancellation scheduled, you won\'t be charged.' : '. Cancel before then to avoid the first charge.'}
                    </span>
                  </div>
                </div>
              )}
              {!sub.in_trial && sub.cancel_at_period_end && (
                <div data-testid="cancellation-banner" className="p-3 rounded-lg border border-[#8A9A92]/30 bg-[#8A9A92]/8 text-[11px] text-[#8A9A92] leading-relaxed">
                  Subscription canceled. You&apos;ll keep Pro access until {sub.pro_until ? fmtDate(sub.pro_until) : 'the end of the current period'}.
                </div>
              )}
              {sub.payment_failed_at && (
                <div data-testid="payment-failed-banner" className="p-3 rounded-lg border border-red-400/40 bg-red-400/10 text-[11px] text-red-200 leading-relaxed">
                  We couldn&apos;t charge your card. Open Manage Billing to update your payment method.
                </div>
              )}
              <button
                data-testid="manage-billing-button"
                onClick={openBillingPortal}
                disabled={!!busy}
                className="w-full py-3 rounded-full bg-[#5C9E8C] hover:bg-[#72C2AC] text-[#08120F] font-medium text-sm transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy === 'portal' ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                Manage Billing (cancel, update card, invoices)
              </button>
              {!sub.cancel_at_period_end && (
                <button
                  data-testid="cancel-subscription-button"
                  onClick={cancelSubscription}
                  disabled={!!busy}
                  className="w-full py-2 rounded-full text-[11px] text-[#8A9A92] hover:text-[#E8E3D9] transition-colors disabled:opacity-50"
                >
                  or, cancel here in one click
                </button>
              )}
            </div>
          )}

          {/* Feature list */}
          <div className="mt-6 pt-4 border-t border-[rgba(92,158,140,0.15)]">
            <div className="label-tiny mb-3">Pro unlocks</div>
            <ul className="space-y-1.5 text-sm">
              {proFeatures.map((f) => (
                <li key={f} className="flex items-center gap-2 text-[#E8E3D9]">
                  <Check size={14} className={sub.pro ? 'text-[#72C2AC]' : 'text-[#8A9A92]'} />
                  <span className={sub.pro ? '' : 'text-[#8A9A92]'}>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Change password */}
        <div className="glass p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings size={14} className="text-[#72C2AC]" />
            <div className="label-tiny">Change Password</div>
          </div>
          <form onSubmit={changePassword} className="space-y-3 max-w-sm">
            <div className="relative">
              <input
                data-testid="current-password-input"
                type={showCurPw ? 'text' : 'password'} required value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                placeholder="Current password"
                className="w-full bg-transparent border-b border-[rgba(92,158,140,0.25)] focus:border-[#72C2AC] outline-none py-2 pr-9 text-[#E8E3D9] text-sm"
              />
              <button
                type="button"
                data-testid="current-password-toggle"
                onClick={() => setShowCurPw((v) => !v)}
                aria-label={showCurPw ? 'Hide current password' : 'Show current password'}
                aria-pressed={showCurPw}
                tabIndex={-1}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-[#8A9A92] hover:text-[#C9DED6] transition-colors"
              >
                {showCurPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="relative">
              <input
                data-testid="new-password-input"
                type={showNewPw ? 'text' : 'password'} required minLength={6} value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="New password (min 6 chars)"
                className="w-full bg-transparent border-b border-[rgba(92,158,140,0.25)] focus:border-[#72C2AC] outline-none py-2 pr-9 text-[#E8E3D9] text-sm"
              />
              <button
                type="button"
                data-testid="new-password-toggle"
                onClick={() => setShowNewPw((v) => !v)}
                aria-label={showNewPw ? 'Hide new password' : 'Show new password'}
                aria-pressed={showNewPw}
                tabIndex={-1}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-[#8A9A92] hover:text-[#C9DED6] transition-colors"
              >
                {showNewPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <button
              data-testid="change-password-button"
              type="submit" disabled={busy === 'pw'}
              className="px-5 py-2 rounded-full bg-[#5C9E8C] hover:bg-[#72C2AC] text-[#08120F] text-sm font-medium transition-colors disabled:opacity-50"
            >
              {busy === 'pw' ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </div>

        {/* Billing history */}
        <div className="glass p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Receipt size={14} className="text-[#72C2AC]" />
            <div className="label-tiny">Billing History</div>
          </div>
          {tx.length === 0 ? (
            <div className="text-xs text-[#8A9A92]">No transactions yet.</div>
          ) : (
            <div className="space-y-2">
              {tx.map((t) => (
                <div key={t.session_id} className="glass-soft p-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-[#E8E3D9] capitalize">{t.plan} plan</div>
                    <div className="text-[11px] text-[#8A9A92]">{fmtDate(t.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[#E8E3D9]">{fmtMoney(t.amount, t.currency)}</div>
                    <div className={`text-[11px] flex items-center gap-1 justify-end ${
                      t.payment_status === 'paid' ? 'text-[#72C2AC]' : 'text-[#8A9A92]'
                    }`}>
                      {t.payment_status === 'paid' ? <Check size={11} /> : <X size={11} />}
                      {t.payment_status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Email preferences — user can pause / weekly-adjust the
            re-engagement nudge emails without leaving the app. */}
        <NudgePreferencesCard />

        {/* Admin: Security dashboard tile */}
        {sub.is_admin && security && (
          <div className="glass p-6 border border-[#C4A67A]/30" data-testid="admin-security-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[#C4A67A]" />
                <div className="label-tiny text-[#C4A67A]">Admin · Security</div>
              </div>
              {security.new_users_24h > 0 && (
                <div
                  data-testid="admin-new-users-badge"
                  className="text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full border border-[#72C2AC]/40 bg-[#72C2AC]/15 text-[#72C2AC] font-mono"
                  title="New users registered in the last 24h"
                >
                  {security.new_users_24h} new user{security.new_users_24h === 1 ? '' : 's'} · 24h
                </div>
              )}
            </div>

            {/* Counter grid — last_hour / 24h / 7d totals per event class.
                Live polled every 30s. Counter is in-memory so it resets on
                backend restart (audit_log persists, this is purely a tile). */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="admin-security-counters">
              {[
                { key: 'failed_logins', label: 'Failed logins', accent: '#E07A5F' },
                { key: 'webhook_signature_rejections', label: 'Webhook sig rejects', accent: '#E07A5F' },
                { key: 'ai_throttle_hits', label: 'AI throttle hits', accent: '#C4A67A' },
                { key: 'session_revocations', label: 'Session revocations', accent: '#72C2AC' },
                { key: 'successful_logins', label: 'Successful logins', accent: '#72C2AC' },
                { key: 'registrations', label: 'New registrations', accent: '#72C2AC' },
                { key: 'throttle_hits', label: 'Other throttle hits', accent: '#8A9A92' },
              ].map(({ key, label, accent }) => {
                const m = security.metrics[key] || { last_hour: 0, last_24h: 0, last_7d: 0 };
                return (
                  <div
                    key={key}
                    data-testid={`security-counter-${key}`}
                    className="rounded-lg border border-[#5C9E8C]/15 bg-black/20 p-3"
                  >
                    <div className="text-[10px] tracking-widest uppercase text-[#8A9A92]">{label}</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-mono text-2xl" style={{ color: accent }}>{m.last_hour}</span>
                      <span className="text-[10px] text-[#5A6B65] font-mono">/ 1h</span>
                    </div>
                    <div className="text-[10px] text-[#5A6B65] font-mono mt-0.5">
                      {m.last_24h} · 24h &nbsp;·&nbsp; {m.last_7d} · 7d
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recent audit events feed — default snapshot shows the 12 most
                recent across all event types. Admin can widen the window via
                the 7d / 30d / 90d chips (backend expands the cap to 300 rows
                and filters by ts). Latest badge shows how many events came
                back so the admin knows they're not silently truncated. */}
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="label-tiny text-[#8A9A92]">Recent activity</div>
              <div className="flex items-center gap-1 text-[10px] font-mono">
                {[
                  { key: null, label: 'Latest' },
                  { key: 7,    label: '7d'     },
                  { key: 30,   label: '30d'    },
                  { key: 90,   label: '90d'    },
                ].map((opt) => {
                  const isActive = activityRangeDays === opt.key;
                  return (
                    <button
                      key={String(opt.key)}
                      data-testid={`admin-activity-range-${opt.key ?? 'latest'}`}
                      onClick={() => { setActivityRangeDays(opt.key); loadSecurity(opt.key); }}
                      className={`px-2 py-1 rounded-md tracking-widest uppercase transition-colors ${
                        isActive
                          ? 'bg-[#5C9E8C]/25 text-[#72C2AC] border border-[#5C9E8C]/40'
                          : 'text-[#8A9A92] hover:text-[#E8E3D9] border border-transparent'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
                {activityRangeDays && (
                  <span
                    className="ml-2 text-[10px] text-[#5A6B65]"
                    data-testid="admin-activity-count"
                  >
                    {security.recent_returned ?? (security.recent_events || []).length} events
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-1.5 max-h-56 overflow-y-auto custom-scrollbar pr-1" data-testid="admin-recent-events">
              {(security.recent_events || []).map((ev) => (
                <div
                  key={ev.id}
                  className="flex items-center gap-3 text-[11px] font-mono py-1 border-b border-[#5C9E8C]/8"
                >
                  <span className="text-[#5A6B65] tabular-nums w-[130px] shrink-0" title={new Date(ev.ts).toLocaleString()}>
                    {new Date(ev.ts).toLocaleDateString([], { month: 'short', day: '2-digit' })}
                    {' · '}
                    {new Date(ev.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                  <span className="text-[#C4A67A] w-[170px] shrink-0 truncate">{ev.event}</span>
                  <span className="text-[#E8E3D9] flex-1 truncate">{ev.user_email || ev.user_id || '—'}</span>
                  <span className="text-[#5A6B65] truncate hidden md:block max-w-[120px]">{ev.ip || ''}</span>
                </div>
              ))}
              {(!security.recent_events || security.recent_events.length === 0) && (
                <div className="text-[11px] text-[#5A6B65] italic">No events yet.</div>
              )}
            </div>
          </div>
        )}

        {/* Phase 4: Harmonic Blueprint — accumulated resonance profile,
            confirmed points, latest journey, and drift-over-time chart.
            Visible to all users; the section itself gracefully handles
            free tier (Pro-only affordances hidden). */}
        <HarmonicBlueprintSection
          isPro={!!(sub.pro || sub.is_admin)}
          onOpenSheet={onOpenHarmonicBlueprint}
        />

        {/* Longitudinal wellness memory — the same session history the
            Wellness Assistant references when it says "Last time you felt X,
            Y helped — want to start there?". Rendered for all users. */}
        <MyJourneySection />

        {/* Detected behavioural patterns — the recurring behaviours the
            Wellness Assistant may reference proactively on app open. User
            can dismiss individual patterns or clear all dismissals. */}
        <MyPatternsSection />

        {/* Admin: Sound Lineage timeline */}
        {sub.is_admin && <SoundLineage />}

        {/* Admin: plan prices */}
        {sub.is_admin && (
          <div className="glass p-6 border border-[#C4A67A]/30" data-testid="admin-prices-card">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={14} className="text-[#C4A67A]" />
              <div className="label-tiny text-[#C4A67A]">Admin · Plan Prices</div>
            </div>
            {/* Promo Codes management — sits inside the admin section so free
                users never see the section header. */}
            <div className="mb-6">
              <PromoCodesSection />
            </div>
            <form onSubmit={saveAdminPrices} className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl">
              <div>
                <label className="label-tiny block mb-1">Monthly (USD)</label>
                <input
                  data-testid="admin-monthly-price-input"
                  type="number" step="0.01" min="0.5" required
                  value={monthly} onChange={(e) => setMonthly(e.target.value)}
                  className="w-full bg-transparent border-b border-[rgba(196,166,122,0.3)] focus:border-[#C4A67A] outline-none py-2 text-[#E8E3D9] font-mono"
                />
              </div>
              <div>
                <label className="label-tiny block mb-1">Annual (USD)</label>
                <input
                  data-testid="admin-annual-price-input"
                  type="number" step="0.01" min="0.5" required
                  value={annual} onChange={(e) => setAnnual(e.target.value)}
                  className="w-full bg-transparent border-b border-[rgba(196,166,122,0.3)] focus:border-[#C4A67A] outline-none py-2 text-[#E8E3D9] font-mono"
                />
              </div>
              <div>
                <label className="label-tiny block mb-1">Trial days</label>
                <input
                  data-testid="admin-trial-days-input"
                  type="number" step="1" min="0" max="90" required
                  value={trial} onChange={(e) => setTrial(e.target.value)}
                  className="w-full bg-transparent border-b border-[rgba(196,166,122,0.3)] focus:border-[#C4A67A] outline-none py-2 text-[#E8E3D9] font-mono"
                />
              </div>
              <div className="md:col-span-3">
                <button
                  data-testid="admin-save-prices-button"
                  type="submit" disabled={busy === 'admin'}
                  className="px-5 py-2 rounded-full bg-[#C4A67A] hover:bg-[#d6b88c] text-[#08120F] text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {busy === 'admin' ? 'Saving…' : 'Save prices'}
                </button>
              </div>
            </form>
            <AnnouncementComposerSection />
          </div>
        )}

        {/* Admin: user management */}
        {sub.is_admin && (
          <div className="glass p-6 border border-[#C4A67A]/30 mt-6" data-testid="admin-users-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-[#C4A67A]" />
                <div className="label-tiny text-[#C4A67A]">Admin · User Management</div>
              </div>
              {/* Real-count header — surface how many users match the
                  current filter so the admin knows they're seeing
                  everyone, not a silently-truncated slice. */}
              <div className="flex items-center gap-3 text-[11px] text-[#8A9A92]" data-testid="admin-users-total">
                <span>
                  Showing{' '}
                  <span className="text-[#E8E3D9]">
                    {users.length ? `${usersMeta.offset + 1}–${usersMeta.offset + users.length}` : '0'}
                  </span>
                  {' of '}
                  <span className="text-[#C4A67A]">{usersMeta.total}</span>
                  {userQuery ? ' matching' : ' registered'}
                </span>
                {usersMeta.filtered_test_count > 0 && (
                  <label
                    className="inline-flex items-center gap-1 cursor-pointer select-none"
                    title={`Include ${usersMeta.filtered_test_count} @example.com pytest-seeded users`}
                  >
                    <input
                      type="checkbox"
                      data-testid="admin-users-include-test"
                      checked={showTestUsers}
                      onChange={(e) => {
                        setShowTestUsers(e.target.checked);
                        loadUsers(userQuery, 0, e.target.checked);
                      }}
                      className="accent-[#C4A67A]"
                    />
                    include test ({usersMeta.filtered_test_count})
                  </label>
                )}
              </div>
            </div>

            <form onSubmit={searchUsers} className="flex items-center gap-2 mb-4 max-w-md">
              <Search size={14} className="text-[#8A9A92]" />
              <input
                data-testid="admin-user-search-input"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Search by email…"
                className="flex-1 bg-transparent border-b border-[rgba(196,166,122,0.3)] focus:border-[#C4A67A] outline-none py-2 text-[#E8E3D9] text-sm"
              />
              <button
                data-testid="admin-user-search-button"
                type="submit"
                className="text-[11px] text-[#C4A67A] hover:text-[#72C2AC] px-3 py-1 transition-colors"
              >
                Search
              </button>
            </form>

            {users.length === 0 ? (
              <div className="text-xs text-[#8A9A92]">No users found.</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
                {users.map((u) => (
                  <div
                    key={u.id}
                    data-testid={`admin-user-row-${u.id}`}
                    className="glass-soft p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-[#E8E3D9] truncate">{u.name || '—'}</div>
                      <div className="text-[11px] text-[#8A9A92] truncate">{u.email}</div>
                      <div className="text-[10px] mt-1 flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded-full ${
                          u.pro ? 'bg-[#C4A67A]/20 text-[#C4A67A]' : 'bg-[#1A332A]/60 text-[#8A9A92]'
                        }`}>
                          {u.pro ? `PRO · ${u.days_left}d` : 'BASIC'}
                        </span>
                        {u.role === 'admin' && (
                          <span className="px-1.5 py-0.5 rounded-full bg-[#5C9E8C]/20 text-[#72C2AC]">ADMIN</span>
                        )}
                        {u.cancelling && (
                          <span
                            data-testid={`user-cancelling-badge-${u.id}`}
                            title="Auto-renew is off. User keeps Pro access until period end."
                            className="px-1.5 py-0.5 rounded-full bg-[#D9A45C]/15 text-[#D9A45C] border border-[#D9A45C]/40"
                          >
                            CANCELLING
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        data-testid={`profile-user-${u.id}`}
                        onClick={() => setProfileModalUserId(u.id)}
                        title="Open full profile editor"
                        className="p-1.5 rounded-full text-[#8A9A92] hover:text-[#72C2AC] hover:bg-[#72C2AC]/10 transition-colors"
                      >
                        <UserCog size={14} />
                      </button>
                      <input
                        data-testid={`grant-days-${u.id}`}
                        type="number" min="1" max="3650"
                        value={grantDays[u.id] ?? '365'}
                        onChange={(e) => setGrantDays({ ...grantDays, [u.id]: e.target.value })}
                        className="w-16 bg-transparent border-b border-[rgba(196,166,122,0.3)] focus:border-[#C4A67A] outline-none py-1 text-[#E8E3D9] text-xs font-mono text-center"
                        title="Days to add"
                      />
                      <span className="text-[10px] text-[#8A9A92]">days</span>
                      <button
                        data-testid={`grant-pro-${u.id}`}
                        onClick={() => grantPro(u.id)}
                        disabled={busy === `grant-${u.id}`}
                        className="px-3 py-1.5 rounded-full bg-[#C4A67A]/20 hover:bg-[#C4A67A]/40 border border-[#C4A67A]/40 text-[#C4A67A] text-[11px] transition-colors disabled:opacity-50"
                      >
                        {busy === `grant-${u.id}` ? '…' : (u.pro ? 'Extend' : 'Grant Pro')}
                      </button>
                      {u.pro && (
                        <button
                          data-testid={`revoke-pro-${u.id}`}
                          onClick={() => revokePro(u.id, u.email)}
                          disabled={busy === `revoke-${u.id}`}
                          className="px-3 py-1.5 rounded-full border border-[#D96C6C]/40 text-[#D96C6C] text-[11px] hover:bg-[#D96C6C]/10 transition-colors disabled:opacity-50"
                        >
                          {busy === `revoke-${u.id}` ? '…' : 'Revoke'}
                        </button>
                      )}
                      {u.role !== 'admin' && (
                        <button
                          data-testid={`delete-user-${u.id}`}
                          onClick={() => deleteUser(u.id, u.email)}
                          disabled={busy === `del-${u.id}`}
                          title="Delete user permanently"
                          className="p-1.5 rounded-full text-[#8A9A92] hover:text-[#D96C6C] hover:bg-[#D96C6C]/10 transition-colors disabled:opacity-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Pagination — only meaningful when there's more than one
                page. Prev/Next drive `loadUsers(userQuery, offset,
                showTestUsers)` so search + include-test filters carry
                across pages. */}
            {usersMeta.total > usersMeta.limit && (
              <div className="flex items-center justify-between mt-4" data-testid="admin-users-pagination">
                <button
                  type="button"
                  data-testid="admin-users-prev"
                  onClick={() => loadUsers(userQuery, Math.max(0, usersMeta.offset - usersMeta.limit), showTestUsers)}
                  disabled={usersMeta.offset === 0}
                  className="text-[11px] uppercase tracking-[0.12em] text-[#C4A67A] hover:text-[#72C2AC] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  ← Prev
                </button>
                <div className="text-[10px] text-[#8A9A92] uppercase tracking-wider">
                  Page {Math.floor(usersMeta.offset / usersMeta.limit) + 1}
                  {' of '}
                  {Math.max(1, Math.ceil(usersMeta.total / usersMeta.limit))}
                </div>
                <button
                  type="button"
                  data-testid="admin-users-next"
                  onClick={() => loadUsers(userQuery, usersMeta.offset + usersMeta.limit, showTestUsers)}
                  disabled={usersMeta.offset + users.length >= usersMeta.total}
                  className="text-[11px] uppercase tracking-[0.12em] text-[#C4A67A] hover:text-[#72C2AC] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Admin: Support Inbox — browse the support_messages collection,
            reply inline via Resend, mark tickets resolved. Sits at the
            bottom of the admin section so the most operational tool is
            right where the admin ends up scrolling to. */}
        {sub.is_admin && <AdminSupportInbox />}

        {/* HF-049 Admin: Cancellations — list + stats + CSV export for the
            db.cancellations collection so churn analytics live next to the
            support inbox where the admin already operates. */}
        {sub.is_admin && <AdminCancellationsInbox />}

        {/* Admin: Email Engagement — re-engagement nudge analytics. */}
        {sub.is_admin && <AdminEmailEngagement />}

        {/* Admin: SMS Pipeline — sent/delivered/failed + consent + recent
            sends tile. Sits right after AdminEmailEngagement so email + SMS
            program health live side-by-side on the admin scroll. */}
        {sub.is_admin && <AdminSMSStats />}

        {/* Admin: Per-frequency ideal default volume editor — lets the
            admin tune the starting tone volume applied when a given
            frequency begins playback, without a code change. */}
        {sub.is_admin && <AdminFrequencyDefaults />}

        {/* Admin: Full profile editor modal. Mounted at the top of the
            admin section so state persists across scrolls; opens when the
            admin taps the UserCog button on any user row. */}
        {sub.is_admin && (
          <AdminUserProfileModal
            open={!!profileModalUserId}
            userId={profileModalUserId}
            onClose={() => setProfileModalUserId(null)}
            onSaved={() => {
              // Refresh the users list so name / role / plan_notes edits
              // are visible in the row immediately.
              loadUsers(userQuery, usersMeta.offset || 0, showTestUsers);
            }}
          />
        )}

        {/* HF-043 Cancel service — placed at the very bottom of the account
            dashboard so it never overshadows the primary actions. Distinct
            from "Cancel subscription" (which downgrades to Basic): this
            closes the WHOLE account. User is auto-reactivated on next
            successful login. */}
        {!sub.is_admin && (
          <div
            data-testid="account-cancel-section"
            className="glass p-6 border border-[#D96C6C]/25"
          >
            <div className="label-tiny mb-2 text-[#D96C6C]">Cancel service</div>
            <div className="text-[14px] text-[#E8E3D9] leading-relaxed mb-2">
              Close your Solarisound account.
            </div>
            <div className="text-[12px] text-[#8A9A92] leading-relaxed mb-4">
              Any active Pro subscription will cancel at the end of the current billing period. You'll continue to receive occasional emails and texts from us about coming back — you can opt out of those from any message. Your Harmonic Blueprint, journeys, and settings stay saved, so signing in again will reactivate everything exactly where you left off.
            </div>
            <button
              data-testid="account-cancel-service-button"
              onClick={() => setCloseModalOpen(true)}
              disabled={!!busy}
              className="text-[13px] px-4 py-2 rounded-full border border-[#D96C6C]/40 text-[#D96C6C] hover:bg-[#D96C6C]/10 transition-colors disabled:opacity-40"
            >
              Cancel service
            </button>
          </div>
        )}
      </div>

      {closeModalOpen && (
        <CancelAccountModal
          onCancel={() => setCloseModalOpen(false)}
          onConfirm={(reason) => {
            setCloseModalOpen(false);
            cancelAccount(reason);
          }}
          busy={busy === 'cancel-account'}
          hasActiveSub={!!(sub.pro && !sub.is_admin)}
        />
      )}

      <CancelSubscriptionModal
        open={cancelSubModalOpen}
        onClose={() => setCancelSubModalOpen(false)}
        onCancelled={onSubscriptionCancelled}
      />

      {celebratingPlan && (
        <ThankYouCelebration
          planLabel={celebratingPlan}
          onStart={() => {
            setCelebratingPlan(null);
            onBack();
          }}
        />
      )}

      {compRedeemed && (
        <ThankYouCelebration
          planLabel="Pro (complimentary)"
          onStart={() => {
            setCompRedeemed(false);
            onBack();
          }}
        />
      )}

      {paymentLinkUrl && (
        <PaymentLinkModal
          url={paymentLinkUrl}
          planLabel={selectedPlan === 'annual' ? 'Pro Annual' : 'Pro Monthly'}
          onClose={() => setPaymentLinkUrl(null)}
        />
      )}
    </div>
  );
}
